import React, { useEffect } from 'react';
import './table.css';
import LoadingBar from '../helper/LoadingBar';
import NoDataAvailable from '../helper/NoDataAvailable';

function Table({
  items,
  columnMap,
  onView,
  onEdit,
  onDelete,
  isLoading,
  currentPage,
  totalPages,
  onPageChange,
  onLimitChange,
  limit,
  showViewButton = false, // Prop untuk mengatur visibilitas "ada-btn-view"
  showEditButton = false, // Prop untuk mengatur visibilitas "tanpa-btn-view"
  showViewOnlyButton = false,
}) {
  const columns = Object.keys(columnMap);

  const handleLimitChange = (e) => {
    onLimitChange(parseInt(e.target.value));
  };

  const renderPaginationItems = () => {
    const pageNumbers = [];
    const maxVisiblePages = 4; // Maximum visible page numbers including current page

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      let startPage, endPage;
      if (currentPage <= 4) {
        startPage = 1;
        endPage = 5;
      } else if (currentPage + 3 >= totalPages) {
        startPage = totalPages - 4;
        endPage = totalPages;
      } else {
        startPage = currentPage - 2;
        endPage = currentPage + 2;
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (startPage > 1) {
        if (startPage > 2) {
          pageNumbers.unshift('...');
        }
        pageNumbers.unshift(1);
      }

      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pageNumbers.push('...');
        }
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers.map((page, index) => (
      <li
        key={index}
        className={`page-item ${page === currentPage ? 'active' : ''} ${
          page === '...' ? 'disabled' : ''
        }`}
      >
        {page === '...' ? (
          <span className="page-link">...</span>
        ) : (
          <button className="page-link" onClick={() => onPageChange(page)}>
            {page}
          </button>
        )}
      </li>
    ));
  };

  useEffect(() => {
    if (currentPage > totalPages) {
      onPageChange(totalPages);
    }
  }, [currentPage, totalPages, onPageChange]);

  // Fungsi untuk memformat angka dengan pemisah ribuan
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  return (
    <div>
      <div className="table-responsive">
        <table className="table table-bordered table-hover table-sm">
          <thead className="table-secondary text-nowrap">
            <tr>
              {columns.map((key, index) => (
                <th key={index} className="">
                  {columnMap[key]}
                </th>
              ))}
              <th className="">Action</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={columns.length + 1} className="text-center">
                  <LoadingBar />
                </td>
              </tr>
            ) : !items || items.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="text-center">
                  <NoDataAvailable />
                </td>
              </tr>
            ) : (
              items.map((item, rowIndex) => (
                <tr key={rowIndex}>
                  {columns.map((key, colIndex) => (
                    <td
                      key={colIndex}
                      className={`table-data ${
                        key === 'rjumlah' || key === 'total_cost'
                          ? 'right-align'
                          : ''
                      }`}
                      title={
                        // Tampilkan tooltip hanya jika teks terpotong dengan ellipsis
                        typeof item[key] === 'string' && item[key].length > 20 // Sesuaikan batas panjang teks sesuai kebutuhan
                          ? item[key]
                          : ''
                      }
                    >
                      {key === 'rjumlah' || key === 'total_cost'
                        ? formatNumber(item[key]) // Format rjumlah dengan pemisah ribuan
                        : typeof item[key] === 'string' &&
                          item[key].match(
                            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/
                          )
                        ? new Date(item[key]).toLocaleDateString('id-ID') // Format tanggal
                        : item[key]}
                    </td>
                  ))}
                  <td className="table-data">
                    {showViewButton && (
                      <div className="ada-btn-view">
                        <i
                          className="bi bi-eye btn btn-info btn-sm"
                          onClick={() => onView(item.id)}
                        ></i>
                        <i
                          className="bi bi-trash btn btn-danger btn-sm ms-2"
                          onClick={() => onDelete(item.id)}
                        ></i>
                      </div>
                    )}
                    {showEditButton && (
                      <div className="tanpa-btn-view">
                        <i
                          className="bi bi-pencil btn btn-info btn-sm"
                          onClick={() => onEdit(item.id)}
                        ></i>
                        <i
                          className="bi bi-trash btn btn-danger btn-sm ms-2"
                          onClick={() => onDelete(item.id)}
                        ></i>
                      </div>
                    )}
                    {showViewOnlyButton && (
                      <div className="ada-btn-view d-flex justify-content-center align-items-center">
                        <i
                          className="bi bi-eye btn btn-info btn-sm"
                          onClick={() => onView(item.id)}
                        ></i>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="pagination-container">
        <div className="d-flex justify-content-between align-items-center mt-3 custom-font-size">
          <div className="d-flex align-items-center">
            <div className="d-flex align-items-center limit">
              <label htmlFor="limitSelect" className="me-2">
                Show
              </label>
              <select
                id="limitSelect"
                value={limit}
                onChange={handleLimitChange}
                className="form-select"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
          <nav aria-label="Page navigation">
            <ul className="pagination mb-0 ">
              <li
                className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}
              >
                <button
                  className="page-link"
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={!currentPage}
                >
                  Previous
                </button>
              </li>
              {renderPaginationItems()}
              <li
                className={`page-item ${
                  currentPage === totalPages ? 'disabled' : ''
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={!currentPage}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  );
}

export default Table;
